import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col, Card, Button, Badge } from "react-bootstrap";
import PropertyBookModal from "./PropertyBookModal";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useMotionValue } from "framer-motion";

// icon
import Icon from "@mdi/react";
import {
  mdiMapMarkerRadiusOutline,
  mdiBedKingOutline,
  mdiShower,
  mdiArrowRightThin,
  mdiShieldLockOutline,
  mdiWifi,
  mdiCarSide,
  mdiHeartOutline,
  mdiHeart,
  mdiPool,
  mdiDumbbell,
  mdiFlower,
  mdiMovieOpenOutline,
  mdiGarage,
  mdiWashingMachine,
  mdiStepForward,
  mdiHomeFloor2,
  mdiHomeFloor3,
} from "@mdi/js";
// data
import PopularDealsData from "./property-data.json";
export default function PopularDeals() {
  const [PopularDealsList] = useState(PopularDealsData);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const angle = useMotionValue(0);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    let frameId;

    const animate = () => {
      const next = (angle.get() + 1) % 360;
      angle.set(next);
      if (el) el.style.setProperty("--gradient-angle", `${next}deg`);
      frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [angle]);

  // Popup
  const [showProperty, setShowProperty] = useState(false);
  const handleCloseProperty = () => setShowProperty(false);
  const handleShowProperty = () => setShowProperty(true);

  // icon fill
  const [likedDeals, setLikedDeals] = useState(
    PopularDealsList.PopularDeals.map(() => false)
  );
  const handleIconClick = (index) => {
    const updatedLikes = likedDeals.map((liked, i) =>
      i === index ? !liked : liked
    );
    setLikedDeals(updatedLikes);
  };

  // property icon
  const getIconPath = (iconName) => {
    switch (iconName) {
      case "mdiWashingMachine":
        return mdiWashingMachine;
      case "mdiGarage":
        return mdiGarage;
      case "mdiMovieOpenOutline":
        return mdiMovieOpenOutline;
      case "mdiFlower":
        return mdiFlower;
      case "mdiDumbbell":
        return mdiDumbbell;
      case "mdiPool":
        return mdiPool;
      case "mdiBedKingOutline":
        return mdiBedKingOutline;
      case "mdiShower":
        return mdiShower;
      case "mdiShieldLockOutline":
        return mdiShieldLockOutline;
      case "mdiWifi":
        return mdiWifi;
      case "mdiCarSide":
        return mdiCarSide;
      default:
        return mdiShower;
    }
  };
  const getBadgeBackgroundColor = (badgeDisplay) => {
    switch (badgeDisplay) {
      case "For Sale":
        return "#4CB6AA";
      case "Sold Out":
        return "#E34949";
      case "For Rent":
        return "#0dcaf0";
      case "coming ":
        return "#E3C814";
      case "New Listing":
        return "#212529";
      default:
        return "#212529";
    }
  };

  const titleRef = useRef(null);

  useEffect(() => {
    const titleEl = titleRef.current; // Cache ref
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-lines");
          } else {
            entry.target.classList.remove("animate-lines");
          }
        });
      },
      { threshold: 0.3 }
    );

    if (titleEl) observer.observe(titleEl);

    return () => {
      if (titleEl) observer.unobserve(titleEl); // Use cached ref
    };
  }, []);

  return (
    <>
      <div>
        {/* Main property section */}
        <section className="section" id="property">
          <Container>
            {/* Section Title */}
            <Row>
              <Col md={12}>
                <div className="section-title-box">
                  <h1 className="section-title" ref={titleRef}>
                    <span className="text-primary">Popular</span> Townhomes
                    {/* Decorative lines for title styling */}
                    <div className="title-line before-line" />
                    <div className="title-line after-line" />
                  </h1>
                </div>
              </Col>
            </Row>

            {/* Property Cards Grid */}
            <Row className="justify-content-center hover-cards-container">
              {PopularDealsData.PopularDeals.map((data, i) => (
                <Col md={6} xl={4} xxl={6} key={i} className="mb-4 d-flex">
                  <motion.div
                    className="hover-card-wrapper position-relative"
                    onMouseEnter={() => setHoveredIndex(i)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    whileHover={{ scale: 1.02, y: -5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <AnimatePresence>
                      {hoveredIndex === i && (
                        <motion.div
                          className="hover-overlay"
                          layoutId="hoverBackground"
                          initial={{ opacity: 0 }}
                          animate={{
                            opacity: 1,
                            transition: { duration: 0.25 },
                          }}
                          exit={{
                            opacity: 0,
                            transition: { duration: 0.2, delay: 0.1 },
                          }}
                        />
                      )}
                    </AnimatePresence>

                    {/* Property Card */}
                    <Card className="deal-card glass-effect overflow-hidden mb-0 rounded-2 shadow-sm w-100 d-flex flex-column">
                    <Card.Body className="p-4 position-relative z-2 d-flex flex-column h-100">
                        <Row className="align-items-center">
                          {/* Property Image Section */}
                          <Col xxl={6}>
                            <div className="deal-img-container position-relative rounded-2 overflow-hidden">
                              {/* Property Status Badge */}
                              <span
                                className={`sale-badge badge-glass`}
                                style={{
                                  backgroundColor: getBadgeBackgroundColor(
                                    data.badgeDisplay
                                  ),
                                  color: "#fff",
                                }}
                              >
                                {data.badgeDisplay}
                              </span>
                              <img src={require(`../../images/property-img/${data.propertyImg}`)} alt="" className="img-fluid w-100 object-fit-cover" style={{ aspectRatio: "4 / 3" }}/>
                            </div>
                          </Col>
                          {/* Property Details Section */}
                          <Col xxl={6}>
                            <div className="d-flex align-items-center justify-content-between mt-4 mt-xxl-0 mb-3">
                              <h4 className="text-primary fw-bold mb-0">
                                {data.propertyPrice}{" "}
                                {/* <span className="fs-14 fw-normal text-white text-opacity-75">Night</span> */}
                              </h4>
                              <div className="text-danger heart-icon cursor-pointer" onClick={() => handleIconClick(i)}>
                                <Icon
                                  path={
                                    likedDeals[i] ? mdiHeart : mdiHeartOutline
                                  }
                                  className="icon-size-20"
                                />
                              </div>
                            </div>
                            {/* Property Title */}
                            <Card.Title className="fs-18 mb-2">
                              {data.propertyTitle}
                            </Card.Title>
                            {/* Property Location */}
                            <div className="d-flex align-items-center text-white text-opacity-85 fs-15 mb-2">
                              <Icon path={mdiMapMarkerRadiusOutline} className="icon-size-40 me-2"/>
                              {data.homeLocation}
                            </div>
                            {/* Property Features Badges */}
<div className="d-flex flex-wrap align-items-center gap-2 mb-3">
  {data.propertyFeatures.map((feature, j) => (
    <Badge
      bg="light"
      className="text-dark fs-13 d-flex align-items-center px-2 py-1 rounded-pill"
      key={j}
    >
      <Icon
        path={getIconPath(feature.icon)}
        className="icon-size-16 me-2"
      />
      {feature.value}
    </Badge>
  ))}
</div>

{/* Available Units */}

<div className="mt-3 unit-section">

    <h6 className="text-primary fw-bold mb-2">
        Available Units
    </h6>

    <div className="d-flex flex-wrap gap-2 unit-chip-container">

        {data.availableUnits &&
            data.availableUnits.map((unit, index) => (
                <span
                    key={index}
                    className="unit-chip"
                >
                    {unit}
                </span>
            ))}

    </div>

    <div className="unit-footer mt-3">

    <span className="unit-total">
        Total Units : {data.totalUnits}
    </span>

</div>

</div>

{/* View More Button */}
<Button
    variant="outline-primary"
    size="sm"
    className="mt-4 align-self-start"
    onClick={handleShowProperty}
>
    View More
</Button>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>

            {/* View More Properties Link */}
           <Row className="mt-5">
  <Col md={12} className="text-center">
    <Button
         variant="outline-primary"
         size="lg"
         onClick={() => window.open("https://docs.google.com/forms/u/1/d/e/1FAIpQLSd_7Mh2uufrf7Jx_mRo14WTi14Z-hW0j3Ay9tZx4Czrw5AfLA/viewform", "_blank")}
    >
      Book Your Townhomes
   </Button>
  </Col>
</Row>
          </Container>
        </section>

        {/* Property Booking Modal */}
        <PropertyBookModal showProperty={showProperty} handleCloseProperty={handleCloseProperty}/>
      </div>
    </>
  );
}
